import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { projectTypesAPI, projectsAPI, usersAPI } from '../utils/api';
import { PageHeader, Modal, FormField, ConfirmDialog, Badge } from '../components/layout/UI';
import { format, isPast, isToday } from 'date-fns';
import { AnyPermissionGate } from '../components/PermissionGate';
import { useToast, apiError } from '../components/Toast';

const PlusIc  = () => <Plus size={14}/>;
const PenIc   = () => <Pencil size={12}/>;
const TrIc    = () => <Trash2 size={12}/>;

// The order here is the order of the status dropdown below, which reads its
// options straight off these keys. It is the same five words, in the same
// order, that tasks and activities use (phase66/69) — a milestone that is
// paused is on Hold like everything else, not "Blocked".
const STATUS_MAP = {
  Pending:     { bg:'bg-slate-100 dark:bg-slate-800',    text:'text-slate-600 dark:text-slate-400',    dot:'bg-slate-400' },
  'In Progress':{ bg:'bg-brand-50 dark:bg-brand-900/20',text:'text-brand-600 dark:text-brand-400', dot:'bg-brand-500' },
  // Sunglow, not red. Red is reserved for errors and destructive actions;
  // a paused milestone is neither.
  Hold:        { bg:'bg-amber-50 dark:bg-amber-900/20',  text:'text-amber-700 dark:text-amber-400',    dot:'bg-amber-400' },
  Completed:   { bg:'bg-emerald-50 dark:bg-emerald-900/20',text:'text-emerald-600 dark:text-emerald-400',dot:'bg-emerald-500' },
  Skipped:     { bg:'bg-slate-50 dark:bg-slate-800/50',  text:'text-slate-400',                        dot:'bg-slate-300' },
};

const defaultForm = { name:'', description:'', target_date:'', owner_id:'', requires_approval:false, is_mandatory:true, status:'Pending', completion_notes:'', approval_status:'' };

export default function MilestonesPage() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const projectId      = searchParams.get('project_id');
  const [milestones, setMilestones]  = useState([]);
  const [projects,   setProjects]    = useState([]);
  const [users,      setUsers]       = useState([]);
  const [selProject, setSelProject]  = useState(projectId||'');
  const [loading,    setLoading]     = useState(false);
  const [modalOpen,  setModalOpen]   = useState(false);
  const [editItem,   setEditItem]    = useState(null);
  const [deleteId,   setDeleteId]    = useState(null);
  const [form,       setForm]        = useState({ ...defaultForm });
  const [saving,     setSaving]      = useState(false);
  const [error,      setError]       = useState('');
  const f = v => setForm(p => ({ ...p, ...v }));

  useEffect(() => {
    Promise.all([projectsAPI.getAll({ limit: 0 }), usersAPI.getAll()])
      .then(([pR,uR]) => { setProjects(pR.data); setUsers(uR.data); });
  }, []);

  const load = useCallback(async () => {
    if (!selProject) return;
    setLoading(true);
    try { const r = await projectTypesAPI.getMilestones(selProject); setMilestones(r.data); setError(''); }
    catch(e) { setError(e.response?.data?.message || 'Failed to load milestones'); } finally { setLoading(false); }
  }, [selProject]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setForm({ ...defaultForm }); setModalOpen(true); };
  const openEdit   = (m) => {
    setEditItem(m);
    setForm({ name:m.name, description:m.description||'', target_date:m.target_date?.split('T')[0]||'',
              owner_id:m.owner_id||'', requires_approval:m.requires_approval, is_mandatory:m.is_mandatory,
              status:m.status, completion_notes:m.completion_notes||'', approval_status:m.approval_status||'' });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name||!selProject) return;
    setSaving(true);
    try {
      if (editItem) await projectTypesAPI.updateMilestone(editItem.id, form);
      else          await projectTypesAPI.createMilestone(selProject, { ...form, sequence_no: milestones.length });
      setModalOpen(false); load();
    } catch(e) { toast.error(apiError(e, 'Failed to save milestone.')); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await projectTypesAPI.deleteMilestone(deleteId); setDeleteId(null); load(); }
    catch(e) { toast.error(apiError(e, 'Failed to delete milestone')); }
  };

  // quickApprove is also gated in the UI by AnyPermissionGate
  const quickApprove = async (m, decision) => {
    try { await projectTypesAPI.updateMilestone(m.id, { approval_status: decision }); load(); }
    catch(e) { toast.error(apiError(e, 'Failed to update approval status')); }
  };

  const dateLabel = (d) => {
    if (!d) return null;
    const date = new Date(d);
    const cls = isPast(date) && !isToday(date) ? 'text-red-500' : isToday(date) ? 'text-amber-500' : 'text-slate-400';
    return <span className={`text-xs ${cls}`}>{format(date,'MMM d, yyyy')}</span>;
  };

  const proj = projects.find(p => p.id === selProject);

  return (
    <div>
      <PageHeader title="Milestones" subtitle="Track project phases and delivery checkpoints"
        actions={selProject && <AnyPermissionGate projectId={selProject} permission="manage_milestones" systemRoles={['Admin','Manager']}><button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIc /> Add Milestone</button></AnyPermissionGate>}/>

      {/* Project selector */}
      <div className="card mb-5">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex-1 min-w-48 max-w-xs">
            <label className="label">Project</label>
            <select className="select" value={selProject} onChange={e => setSelProject(e.target.value)}>
              <option value="">Select a project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {proj && (
            <div className="flex gap-4 text-sm" style={{ color:'var(--text-secondary)' }}>
              <span>Client: <strong style={{ color:'var(--text)' }}>{proj.client}</strong></span>
              <Badge value={proj.status} type="status"/>
              {proj.project_type && <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--surface-alt)]">{proj.project_type}</span>}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm mb-3" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
          ⚠ {error}
        </div>
      )}
      {!selProject ? (
        <div className="card text-center py-16">
          <p className="text-4xl mb-3">🏁</p>
          <p className="font-semibold mb-1" style={{ color:'var(--text)' }}>Select a Project</p>
          <p className="text-sm" style={{ color:'var(--text-secondary)' }}>Choose a project above to view and manage its milestones</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : milestones.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-4xl mb-3">🏁</p>
          <p className="font-semibold mb-1" style={{ color:'var(--text)' }}>No milestones yet</p>
          <p className="text-sm mb-4" style={{ color:'var(--text-secondary)' }}>Add milestones manually or they are auto-generated based on project type</p>
          <AnyPermissionGate projectId={selProject} permission="manage_milestones" systemRoles={['Admin','Manager']}><button onClick={openCreate} className="btn-primary flex items-center gap-1.5 mx-auto"><PlusIc /> Add First Milestone</button></AnyPermissionGate>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Progress bar */}
          <div className="card py-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium" style={{ color:'var(--text)' }}>
                {milestones.filter(m=>m.status==='Completed').length} / {milestones.length} milestones complete
              </span>
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                <div className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{ width:`${milestones.length>0 ? (milestones.filter(m=>m.status==='Completed').length/milestones.length)*100 : 0}%` }}/>
              </div>
              <span className="text-sm font-bold text-emerald-500">
                {milestones.length>0 ? Math.round(milestones.filter(m=>m.status==='Completed').length/milestones.length*100) : 0}%
              </span>
            </div>
          </div>

          {/* Milestone cards */}
          {milestones.map((m, idx) => {
            const sm = STATUS_MAP[m.status]||STATUS_MAP['Pending'];
            const isLate = m.target_date && isPast(new Date(m.target_date)) && m.status!=='Completed' && m.status!=='Skipped';
            return (
              <div key={m.id} className={`card transition-all ${isLate?'border-l-4 border-l-red-500':''}`}>
                <div className="flex items-start gap-4">
                  {/* Sequence & status */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm"
                      style={{ background: m.status==='Completed' ? '#10b981'
                                         : m.status==='Hold'      ? '#FFC849'
                                         : '#2E539F',
                               // Sunglow is a light fill — white on it is
                               // unreadable, so the number goes dark instead.
                               color:      m.status==='Hold' ? '#6b5205' : '#fff' }}>
                      {m.status==='Completed' ? '✓' : m.sequence_no + 1}
                    </div>
                    {idx < milestones.length - 1 && <div className="w-0.5 h-4 rounded" style={{ background:'var(--border)' }}/>}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold" style={{ color:'var(--text)' }}>{m.name}</p>
                          {m.is_mandatory && <span className="text-xs text-red-400 font-medium">Required</span>}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{m.status}</span>
                          {isLate && <span className="text-xs font-bold text-red-500">OVERDUE</span>}
                          {m.requires_approval && m.status!=='Completed' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              m.approval_status==='Approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                              m.approval_status==='Rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                              'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            }`}>
                              {m.approval_status==='Approved' ? '✓ Approved' : m.approval_status==='Rejected' ? '✗ Rejected' : '⏳ Pending Approval'}
                            </span>
                          )}
                        </div>
                        {m.description && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{m.description}</p>}
                        <div className="flex gap-4 mt-1 flex-wrap">
                          {m.target_date && <span className="flex items-center gap-1 text-xs">{dateLabel(m.target_date)}</span>}
                          {m.actual_date  && <span className="text-xs text-emerald-500">Completed: {format(new Date(m.actual_date),'MMM d, yyyy')}</span>}
                          {m.owner_name   && <span className="text-xs flex items-center gap-1" style={{ color:'var(--text-secondary)' }}>
                            <div className="w-4 h-4 rounded text-white text-xs flex items-center justify-center font-bold" style={{ background:m.owner_color||'#2E539F' }}>{m.owner_name[0]}</div>
                            {m.owner_name}
                          </span>}
                        </div>
                        {m.completion_notes && <p className="text-xs mt-1 italic" style={{ color:'var(--text-secondary)' }}>{m.completion_notes}</p>}
                      </div>

                      <div className="flex gap-1.5 flex-shrink-0">
                        {m.requires_approval && !m.approval_status && (
                          <>
                            <AnyPermissionGate projectId={m.project_id} permission="approve_gates" systemRoles={['Admin','Manager']}>
                              <button onClick={()=>quickApprove(m,'Approved')} className="text-xs px-2 py-1 rounded bg-emerald-500 text-white hover:bg-emerald-600">Approve</button>
                              <button onClick={()=>quickApprove(m,'Rejected')} className="text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600">Reject</button>
                            </AnyPermissionGate>
                          </>
                        )}
                        <button onClick={()=>openEdit(m)} className="p-1.5 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><PenIc /></button>
                        <button onClick={()=>setDeleteId(m.id)} className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><TrIc /></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editItem?'Edit Milestone':'Add Milestone'} size="md">
        <div className="space-y-4">
          <FormField label="Milestone Name" required>
            <input className="input" value={form.name} onChange={e=>f({name:e.target.value})} placeholder="e.g. Requirements Gathering Complete"/>
          </FormField>
          <FormField label="Description">
            <textarea className="input" rows={2} value={form.description} onChange={e=>f({description:e.target.value})}/>
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Target Date">
              <input type="date" className="input" value={form.target_date} onChange={e=>f({target_date:e.target.value})}/>
            </FormField>
            <FormField label="Owner">
              <select className="select" value={form.owner_id} onChange={e=>f({owner_id:e.target.value})}>
                <option value="">Unassigned</option>
                {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </FormField>
          </div>
          {editItem && (
            <FormField label="Status">
              <select className="select" value={form.status} onChange={e=>f({status:e.target.value})}>
                {Object.keys(STATUS_MAP).map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
          )}
          {editItem && form.status==='Completed' && (
            <FormField label="Completion Notes">
              <textarea className="input" rows={2} value={form.completion_notes} onChange={e=>f({completion_notes:e.target.value})} placeholder="Notes about how this milestone was completed"/>
            </FormField>
          )}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color:'var(--text)' }}>
              <input type="checkbox" checked={form.requires_approval} onChange={e=>f({requires_approval:e.target.checked})}/> Requires approval
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color:'var(--text)' }}>
              <input type="checkbox" checked={form.is_mandatory} onChange={e=>f({is_mandatory:e.target.checked})}/> Mandatory
            </label>
          </div>
          {editItem && form.requires_approval && (
            <FormField label="Approval Status">
              <select className="select" value={form.approval_status} onChange={e=>f({approval_status:e.target.value})}>
                <option value="">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </FormField>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving||!form.name} className="btn-primary flex items-center gap-2">
              {saving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              {editItem?'Save Changes':'Add Milestone'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={handleDelete}
        title="Delete Milestone" message="Delete this milestone permanently?"/>
    </div>
  );
}
